// import TimesheetFilters from "./filters";
import { HEADER_CONTENT_PORTAL_ID, formatName } from "utils";
import {
  CURRENT_MONTH_START_DATE,
  TIMESHEET_MYPROJECTS_REVIEW,
  formatDateForDisplay,
  CURRENT_MONTH_END_DATE,
} from "@/lib/utils";
import List from "components/list";
import { Portal } from "@radix-ui/react-portal";
import TimeSheetReviewLayout from "@/modules/timesheet/reviews/layout";
import { TimesheetFilters } from "../review/components/filters";
import { TimesheetHeaderTitle } from "@/modules/timesheet/components/header";
import { usePortal } from "hooks";
import { useState } from "react";
import { getUserFields } from "components/user/config";
import { EmployeeCell } from "components/cell/employee/cell";
import { useUpdateTimesheetEntry } from "components/timesheet/review/hooks/useUpdateTimesheetEntry";
import ReviewAlert from "@/modules/timesheet/reviews/review/components/review-alert";
import { toast } from "sonner";
import { eventBus } from "utils/eventBus";
import Icon from "components/icon";
import { CIRCLE_CHECK, INFO } from "icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui/components/tooltip";
import { Button } from "ui/index";
import { TagCell } from "@/modules/work/jobs/timesheet/tags/_cell";
import { invalidateAllQueries } from "utils/utils";
// import TimeSheetReviewLayout from "../../layout";

export default function ProjectTimesheetTableView() {
  const headerPortal = usePortal({ elementId: HEADER_CONTENT_PORTAL_ID });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const updateTimesheetMutation = useUpdateTimesheetEntry({
    onSuccess: () => {
      toast.success("Timesheet reviewed successfully");
      invalidateAllQueries();
      eventBus.emit("refresh:list", "timesheettimesheet-my-jobs");
    },
    onError: (error) => {
      toast.error("Error updating timesheet:", error);
      console.error("Error updating timesheet:", error);
    },
  });

  async function handleUpdateTimesheet() {
    try {
      const item = {
        date: selectedEntry.date,
        approved_hours: selectedEntry.logged_hours,
        unapproved_hours: 0,
      };
      await updateTimesheetMutation.mutateAsync({
        id: selectedEntry.id,
        values: item,
      });

      setShowConfirmDialog(false);
    } catch (error) {
      throw error;
    }
  }

  const columns = [
    {
      id: "index",
      header: "",
      size: 40,
      cell: (info) => {
        const { index, original } = info.row;
        const totalApprovedUnapproved =
          (original.approved_hours || 0) + (original.unapproved_hours || 0);
        const loggedHours = original.logged_hours || 0;
        const showRedDot = totalApprovedUnapproved !== loggedHours;

        return (
          <div className="flex items-center gap-1">
            {showRedDot && (
              <span className="size-2 bg-red-500 rounded-full inline-block"></span>
            )}
          </div>
        );
      },
    },
    {
      id: "date",
      header: "Date",
      accessorKey: "date",
      sortable: true,
      cell: (info) => formatDateForDisplay(info.getValue()),
    },
    {
      id: "employee",
      header: "Employee",
      accessorKey: "employee",
      cell: EmployeeCell,
    },
    {
      id: "project",
      header: "Project",
      accessorKey: "project.name",
    },
    {
      id: "job",
      header: "Job",
      accessorKey: "job.name",
    },
    {
      id: "milestone",
      header: "Milestone",
      accessorKey: "milestone.name",
    },
    {
      id: "tag",
      header: "Tag",
      accessorKey: "tag",
      sortable: true,
      cell: TagCell,
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      cell: (info) => {
        const description = info.getValue();
        if (!description) return "";
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="max-w-30 truncate">{description}</div>
            </TooltipTrigger>
            <TooltipContent className="max-w-50 whitespace-normal break-words">
              {description}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      id: "logged_hours",
      header: "Logged",
      accessorKey: "logged_hours",
      sortable: true,
      cell: (info) => {
        const hours = info.getValue();
        return hours ? hours : "0";
      },
    },
    {
      id: "approved_hours",
      header: "Approved / Unapproved",
      accessorKey: "approved_hours",
      sortable: true,
      cell: (info) => {
        const approvedHours = info.row.original.approved_hours || 0;
        const unapprovedHours = info.row.original.unapproved_hours || 0;
        return approvedHours + " / " + unapprovedHours;
      },
    },
    {
      id: "actions",
      header: "",
      size: 40,
      cell: (info) => {
        const { original } = info.row;
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedEntry(original);
                setShowConfirmDialog(true);
              }}
            >
              <Icon icon={CIRCLE_CHECK} size={10} />
            </Button>
          </div>
        );
      },
    },
  ];

  const fields = [
    "id",
    "description",
    "logged_hours",
    "approved_hours",
    "unapproved_hours",
    "date",
    getUserFields("employee"),
    "project.name",
    "milestone.name",
    "project.id",
    "job.name",
    "job.id",
    "job.coordinator.first_name",
    "tag.id",
    "tag.label",
    "tag.color",
  ];

  const defaultFilters = {
    _unreviewed_hours: { _eq: true },
    logged_hours: { _gt: 0 },
    approved_hours: { _null: true },
    unapproved_hours: { _null: true },
    date: {
      _gte: CURRENT_MONTH_START_DATE,
      _lte: CURRENT_MONTH_END_DATE,
    },
    reviewer: { user: { _eq: "$CURRENT_USER" } },
  };

  return (
    <TimeSheetReviewLayout>
      <Portal container={headerPortal}>
        <TimesheetHeaderTitle>Table</TimesheetHeaderTitle>
      </Portal>
      <List
        storageKey="timesheet-my-jobs"
        title=""
        endPoint="timesheet"
        filters={defaultFilters}
        showSearch={false}
        sortBy="date_updated"
        sortOrder="desc"
        columns={columns}
        meta={{
          fields,
        }}
        renderFilters={TimesheetFilters}
        teleportFilterButton={true}
        hideFilterButton={true}
        teleportFilters={true}
        href={(item) => `/${TIMESHEET_MYPROJECTS_REVIEW}/${item.id}`}
      />
      <ReviewAlert
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleUpdateTimesheet}
        isLoading={updateTimesheetMutation.isPending}
      />
    </TimeSheetReviewLayout>
  );
}
